import React, {useState, useEffect, useRef} from 'react';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {FontSizes, vs, WINDOW_HEIGHT} from '@/Utils/Dimensions';
import CCPackageTable from '@/Atoms/CCPackageTable';
import useGetTournamentStandings from '@/Hooks/TournamentsApi/useGetTournamentStandings';
import {TouchableOpacity, ScrollView, View, StyleSheet} from 'react-native';
import {Image} from 'react-native';
import NoStanding from '@/Assets/Images/Tournaments/no_standing.svg';
import useIsMobile from '@/Hooks/useIsMobile';
import {debounce, getColorFromUsername} from '@/Utils/CommonUtils';
import {Image as RNImage} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import useGetPlayerDetails from '@/Hooks/TournamentsApi/useGetPlayerDetails';
import Pagination from '@/Screens/Games/Pagination';
import RoundNavigator from './RoundNavigator';
import CCLoader from '@/Atoms/CCLoader';

// Amber — no dedicated "warning" color exists in CCColors, so this mirrors
// the spec's suggested late-entry amber directly.
const LATE_ENTRY_COLOR = '#B45309';

// Late-entry label for a standings row: `Late entry · joined {time}`, with a
// short date prefix when the join happened on a different calendar day than
// the late-join threshold (round1_paired_at, falling back to start_date).
// Returns null when the row isn't late or either timestamp is missing/invalid.
const getLateJoinLabel = (
  joinedAt?: string | null,
  threshold?: string | null,
): string | null => {
  if (!joinedAt || !threshold) {
    return null;
  }
  const joinedDate = new Date(joinedAt);
  const thresholdDate = new Date(threshold);
  if (
    Number.isNaN(joinedDate.getTime()) ||
    Number.isNaN(thresholdDate.getTime())
  ) {
    return null;
  }
  if (joinedDate <= thresholdDate) {
    return null;
  }
  const sameDay = joinedDate.toDateString() === thresholdDate.toDateString();
  const time = joinedDate.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (sameDay) {
    return `Late entry · joined ${time}`;
  }
  const datePrefix = joinedDate.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
  });
  return `Late entry · joined ${datePrefix}, ${time}`;
};

const Standings: React.FC<{
  round: number;
  tournamentId: string;
  isConnected: boolean;
  username: string;
  searchterm: string;
  details: {
    timeControl: string;
    tournamentDuration: string;
    ratedGames: string;
    completedGames: number;
    rounds: number;
    players: string;
    players_joined: Array<{
      username: string;
      rating: number;
    }>;
  };
  displayNameTournament?: string;
  setGameId: (gameId: string) => void;
  setActiveTab: (active_tab: string) => void;
  watchTournamentGame: (
    pairingKey: string,
    tournamentId: string,
    round: number,
  ) => void;
  playing: boolean;
  setSearchTab: (searchTab: boolean) => void;
  setShowSearch: (showSearch: boolean) => void;
  viewPlayer: boolean;
  setViewPlayer: (viewPlayer: boolean) => void;
  playerName: string;
  setPlayerName: (playerName: string) => void;
  playerId: string;
  setPlayerId: (playerId: string) => void;
  viewPlayerGame: boolean;
  setViewPlayerGame: (viewPlayerGame: boolean) => void;
  standings: any;
  userId: string | null;
  refreshApi: boolean;
  setPlayerUserId: (id: string | null) => void;
  setRefreshApi: (refreshApi: boolean) => void;
  activeTab: string;
  // ISO timestamp (round1_paired_at, falling back to start_date) — rows whose
  // joined_at is after this are flagged as a late entry. Absent → no badges.
  lateJoinThreshold?: string | null;
  // Narrow-container override for `useIsMobile()`: the table is also mounted
  // inside PlayChessV2's 444-480 px tournament rail on a DESKTOP, where the
  // viewport is wide but the container is phone-sized. Omitted (every existing
  // call site) → the hook decides, exactly as before.
  compact?: boolean;
}> = ({
  round,
  tournamentId,
  isConnected,
  username,
  searchterm,
  details,
  displayNameTournament,
  setGameId,
  setActiveTab,
  watchTournamentGame,
  playing,
  setSearchTab,
  setShowSearch,
  viewPlayer,
  setViewPlayer,
  playerName,
  setPlayerName,
  playerId,
  setPlayerId,
  viewPlayerGame,
  setViewPlayerGame,
  standings,
  userId,
  refreshApi,
  setPlayerUserId,
  setRefreshApi,
  activeTab,
  lateJoinThreshold,
  compact,
}) => {
  const tableRef = useRef(null);
  const viewportIsMobile = useIsMobile();
  const isMobileOrTab = compact === undefined ? viewportIsMobile : compact;
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRound, setCurrentRound] = useState(round);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [rowClicked, setRowClicked] = useState(false);
  // const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    mutate: fetchTournamentStandings,
    data: tournamentStandings,
    isLoading,
    isError,
    error,
  } = useGetTournamentStandings();
  const {
    mutate: fetchPlayerDetails,
    data: playerDetails,
    isPlayerDetailsLoading,
    iserror,
  } = useGetPlayerDetails();
  const handlePressBack = () => {
    setRowClicked(false);
    setViewPlayer(false);
    setSearchTab(true);
    setShowSearch(true);
  };
  // Fetch the tournament details when the component mounts
  const limit = 10;
  const offset = (currentPage - 1) * limit;

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchTerm(searchterm);
    }, 500);

    handler();
  }, [searchterm]);

  useEffect(() => {
    if (refreshApi && userId && activeTab === 'standings') {
      const payload: any = {
        round: currentRound,
        tournamentId,
        limit,
        offset,
        userId,
      };
      setIsRefreshing(true);
      // debugger;
      fetchTournamentStandings(payload, {
        onSuccess: () => {
          setRefreshApi(false);
          setIsRefreshing(false);
        },
        // Without this, a SINGLE failed refresh latches `isRefreshing` true for
        // the lifetime of the mount, and L634's `isRefreshing || isLoading`
        // guard then renders the loader forever — every later, successful
        // response included. Seen on the V2 lane when a pre-start round=0 call
        // 400'd (the tserver pairing validator requires `round > 0`): the tab
        // was still spinning in round 1 while the network showed round=1
        // returning rows. Fix S clamps the round, but any transient failure
        // could wedge the panel the same way.
        onError: () => {
          setRefreshApi(false);
          setIsRefreshing(false);
        },
      });
    }
  }, [refreshApi, activeTab, userId]);

  useEffect(() => {
    if (tournamentId) {
      fetchTournamentStandings({
        round: currentRound,
        tournamentId,
        limit: limit,
        offset: offset,
        userId,
        ...(debouncedSearchTerm ? {search: debouncedSearchTerm} : {}),
      });
      if (!viewPlayer) {
        setSearchTab(true);
        setShowSearch(true);
      }
      if (playerName === '') {
        handlePressBack();
      }
      if (debouncedSearchTerm === '' && playerName === '') {
        handlePressBack();
      }
    }
    if (viewPlayer && playerId) {
      fetchPlayerDetails({uuid: playerId.toString(), tournamentId});
      setSearchTab(false);
      setShowSearch(false);
      setViewPlayerGame(false);
    }
  }, [
    currentRound,
    round,
    isConnected,
    viewPlayer,
    playerId,
    currentPage,
    tournamentId,
    debouncedSearchTerm,
    userId,
  ]);
  const playerData = playerDetails?.data || {};

  useEffect(() => {
    setCurrentRound(round);
  }, [round]);

  const playerTableData = React.useMemo(() => {
    if (!playerData?.opponents || !Array.isArray(playerData?.opponents)) {
      return []; // Return empty array if opponents data is not available
    }

    // console.log(playerData.opponents, 'playerData.opponents');

    return playerData?.opponents?.map((item, index) => {
      const gameId = item?.cc_gameid;
      const serialNo = item?.round;
      const opponentName = item?.player_name;
      const opponentRating = item?.player_rating;
      // console.log("item.result: ", item?.result);
      const result =
        item?.result === '0.5-0.5'
          ? 'D'
          : (item?.parent_id ? item?.result === '0-1' : item?.result === '1-0')
          ? 'L'
          : 'W';

      let itemValues: any = [];

      itemValues?.push({
        name: (
          <Box style={styles.tableRowStyle}>
            {<CCText fontSize={FontSizes[12]}>{serialNo}</CCText>}
          </Box>
        ),
        type: 'text',
      });

      itemValues?.push({
        name: (
          <Box flexDirection="row">
            <Box>
              <View
                style={[
                  styles.nameStyle,
                  {
                    backgroundColor: getColorFromUsername(opponentName),
                  },
                ]}>
                <CCText color={CCColors.White} fontSize={FontSizes[12]}>
                  {opponentName?.substring(0, 2)?.toUpperCase() || ''}
                </CCText>
              </View>
            </Box>

            <Box style={styles.tableRowStyle}>
              {<CCText fontSize={FontSizes[12]}>{opponentName}</CCText>}
            </Box>
          </Box>
        ),
        type: 'text',
      });

      itemValues?.push({
        name: (
          <Box style={styles.tableRowStyle}>
            {
              <CCText color={CCColors.Grey} fontSize={FontSizes[12]}>
                {opponentRating}
              </CCText>
            }
          </Box>
        ),
        type: 'text',
      });

      itemValues?.push({
        name: (
          <Box style={styles.tableRowStyle}>
            {
              <Box
                style={{
                  backgroundColor:
                    result === 'W'
                      ? CCColors.Primary.Default
                      : result === 'L'
                      ? CCColors.Red
                      : CCColors.EvaluationScore.Background,
                  borderRadius: 30,
                  padding: 10,
                }}>
                <CCText
                  color={result === 'W' ? CCColors.Black : CCColors.White}
                  fontWeight="bold"
                  fontSize={FontSizes[13]}>
                  {result}
                </CCText>
              </Box>
            }
          </Box>
        ),
        type: 'text',
      });
      itemValues.gameId = gameId;
      return itemValues;
    });
  }, [playerData?.opponents]);
  // Single source: this component's own paginated / searched / round-scoped
  // standings fetch. Previously a `standings` prop could override it, which
  // ignored pagination, search and round selection and showed a stale list.
  const rawData = tournamentStandings?.data || [];

  const standingsData = rawData?.map((entry, index) => ({
    ...entry,
    position: offset + index + 1,
    yourUserId: username,
  }));
  // const standingsData = fullRankedData.filter(entry =>
  //   entry?.player_name?.toLowerCase().startsWith(searchterm.toLowerCase()),
  // );
  const highlightedRowIndex = !rowClicked
    ? standingsData
        ?.map((item, i) => (item?.cc_userid == username ? i : ''))
        ?.filter(String)
    : null;

  const headers = ['', 'Player', 'Score', 'Tie Break'];

  const rankIcons = {
    0: require('../../../Assets/Badges/1stPrize.png'),
    1: require('../../../Assets/Badges/2ndPrize.png'),
    2: require('../../../Assets/Badges/3rdPrize.png'),
  };

  // Transform standingsData into tableData format
  const allRows = standingsData?.map(item => {
    const position = item?.rank;
    const name = item?.player_name;
    const score = item?.point;
    const tieBreak = `${item?.TB1}/${item?.TB2}/${item?.TB3}`;
    const ccUserId = item?.cc_userid;
    const yourUserId = item?.yourUserId;
    const playerId = item?.player_id;
    const playerTitle = item?.player_title;
    const playerRating = item?.player_rating;
    const index = item?.rank - 1;
    const joinedAt = item?.joined_at;
    const lateJoinLabel = getLateJoinLabel(joinedAt, lateJoinThreshold);

    let itemValues: any = [];

    itemValues?.push({
      name: (
        <Box
          style={{
            // padding: vs(10),
            width: '100%',
            height: '100%',
            alignItems: 'center',
          }}>
          {currentPage === 1 && rankIcons[index] && searchterm === '' ? (
            <RNImage
              source={rankIcons[index]}
              style={{
                width: 24,
                height: 24,
                resizeMode: 'contain',
              }}
            />
          ) : (
            <CCText fontSize={FontSizes[12]}>{position}.</CCText>
          )}
        </Box>
      ),
      type: 'text',
    });

    itemValues?.push({
      name: (
        <Box
          style={{
            padding: vs(10),
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: CCColors?.LightBrownShade,
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: 10,
            }}>
            <CCText
              fontSize={FontSizes[10]}
              style={{color: CCColors?.BrownShade2, fontWeight: '600'}}>
              {name?.substring(0, 2)?.toUpperCase() || ''}
            </CCText>
          </View>
          <View style={{flexDirection: 'column'}}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}>
              {playerTitle && (
                <CCText
                  fontSize={FontSizes[12]}
                  fontWeight="bold"
                  color={CCColors?.Red}
                  style={{marginRight: 4}}>
                  {playerTitle}
                </CCText>
              )}
              <CCText
                fontWeight={ccUserId == yourUserId ? 'bold' : 'regular'}
                fontSize={FontSizes[12]}
                numberOfLines={1}
                style={{maxWidth: 100}}>
                {name}
              </CCText>
            </View>
            <CCText fontSize={FontSizes[10]} fontWeight="medium">
              {playerRating}
            </CCText>
            {lateJoinLabel && (
              // react-native-web's Text/View don't forward a raw HTML
              // `title`; accessibilityLabel (-> aria-label) is the closest
              // supported tooltip-equivalent, carrying the full local
              // date-time.
              <CCText
                fontSize={FontSizes[10]}
                color={LATE_ENTRY_COLOR}
                accessibilityLabel={
                  joinedAt ? new Date(joinedAt).toString() : undefined
                }
                numberOfLines={1}
                style={{maxWidth: 140}}>
                {lateJoinLabel}
              </CCText>
            )}
          </View>
        </Box>
      ),
      type: 'text',
    });

    itemValues?.push({
      name: (
        <Box style={{padding: vs(10), width: '100%', height: '100%'}}>
          <CCText fontSize={FontSizes[14]}>{score}</CCText>
        </Box>
      ),
      type: 'text',
    });

    itemValues.push({
      name: (
        <Box style={{padding: vs(10), width: '100%', height: '100%'}}>
          <CCText fontSize={FontSizes[12]} color={CCColors?.TextColor?.Grey}>
            {tieBreak}
          </CCText>
        </Box>
      ),
      type: 'text',
    });

    return {itemValues, index, ccUserId, yourUserId, playerId, name};
  });
  // const youRow = allRows?.find(row => row?.ccUserId === Number(username));
  // const otherRows = allRows?.filter(row => row?.ccUserId !== Number(username));
  const tableData = allRows?.map(r => r?.itemValues);
  // ? [youRow.itemValues, ...otherRows.map(r => r.itemValues)]
  // : allRows.map(r => r.itemValues);
  // Style adjustments for standings rows
  const customFlexArr = [1, 3, 2, 2];

  // const scrollToPlayer = () => {
  //   const height = highlightedRowIndex * 80;
  //   tableRef?.current?.scrollTo({y: height, animated: true});
  // };

  // useEffect(() => {
  //   if (userId) {
  //     const payload: any = {
  //       round: currentRound,
  //       tournamentId,
  //       limit,
  //       offset,
  //       ...(debouncedSearchTerm ? {search: debouncedSearchTerm} : {}),
  //       userId,
  //     };

  //     if (
  //       highlightedRowIndex !== -1 &&
  //       highlightedRowIndex !== null &&
  //       highlightedRowIndex !== undefined
  //     ) {
  //       scrollToPlayer();
  //     } else {
  //       fetchTournamentStandings(payload, {
  //         onSuccess: (data: any) => {
  //           if (data?.currentPage) {
  //             setCurrentPage(data?.currentPage);
  //           }
  //         },
  //       });
  //     }
  //   }
  // }, [userId, highlightedRowIndex]);

  // Handle row press (e.g., navigate to player details)
  const handleRowPress = (rowIndex: number) => {
    console.log(`Row ${rowIndex} pressed`);
    setRowClicked(true);
    const selectedRow = allRows[rowIndex];
    if (selectedRow) {
      const {playerId, name} = selectedRow;
      console.log(`Row ${rowIndex} pressed, playerId: ${playerId}`);
      setViewPlayer(true);
      setViewPlayerGame(false);
      setSearchTab(false);
      setShowSearch(false);
      setPlayerName(name);
      if (playerId) {
        setPlayerId(playerId);
        fetchPlayerDetails({uuid: playerId?.toString(), tournamentId});
      }
    }
  };
  // const playersWithPosition = (details?.players_joined || [])
  //   .slice() // avoid mutating original
  //   .sort((a, b) => Number(b?.rating) - Number(a?.rating)) // sort by rating desc
  //   .map((player, idx) => ({
  //     ...player,
  //     position: idx + 1, // assign rank based on sorted order
  //   }));
  // const highlightPlayer = playersWithPosition?.find(
  //   a => a?.username === displayNameTournament,
  // );
  // const remainingPlayers = playersWithPosition?.filter(
  //   a => a?.username !== displayNameTournament,
  // );
  const handleRowPressPlayerList = (index: number) => {
    const opponent = playerData?.opponents?.[index];

    if (
      !opponent ||
      opponent?.player_name?.toLowerCase?.() === 'bye' ||
      !opponent?.cc_gameid
    ) {
      return;
    }

    const gameId = opponent?.cc_gameid;
    const tableId = opponent?.table_id;
    const pairingKey =
      opponent?.parent_id === null
        ? `pairing:${opponent?.cc_userid}-${playerData?.details?.cc_userid}`
        : `pairing:${playerData?.details?.cc_userid}-${opponent?.cc_userid}`;

    const createdAt = opponent?.createdAt
      ? new Date(opponent?.createdAt)
      : null;

    const fiveDaysPassed =
      createdAt && Date.now() - createdAt.getTime() > 5 * 24 * 60 * 60 * 1000;

    console.log(gameId, 'playerData.opponents[index].cc_gameid');
    if (!playing) {
      // const tableId = filteredGamesData[rowIndex].tableId;
      if (gameId && fiveDaysPassed) {
        setGameId(gameId);
        setActiveTab('notation');
        setViewPlayerGame(true);
      } else if (pairingKey) {
        console.info('watch tableId: ', pairingKey);
        watchTournamentGame(
          pairingKey,
          opponent?.tournament_id,
          opponent?.round,
        );
        setActiveTab('notation');
      }
    }
  };

  const totalCount = tournamentStandings?.count || 0;
  const totalPages = Math.ceil(totalCount / 10);

  if (isRefreshing || isLoading) {
    return <CCLoader loading={true} isComponentLoader />;
  }
  return (
    <Box style={{height: WINDOW_HEIGHT / 1.85}}>
      <Box
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
          // marginBottom: vs(10),
          marginLeft: vs(8),
          width: isMobileOrTab ? '100%' : '50%',
        }}>
        <RoundNavigator
          totalRounds={round}
          currentRound={currentRound}
          setRound={(newRound: number) => {
            setCurrentRound(newRound);
            setPlayerUserId(null);
          }}
          setCurrentPage={setCurrentPage}
        />
      </Box>
      <ScrollView ref={tableRef}>
        {tableData?.length > 0 ? (
          viewPlayer ? (
            <Box>
              <Box
                flexDirection="row"
                marginLeft={10}
                gap={10}
                onPress={handlePressBack}>
                <MaterialCommunityIcons
                  name="arrow-left"
                  size={18}
                  color={CCColors.Primary.Dark}
                />
                <CCText fontSize={FontSizes[14]}>
                  {playerName}'s pairing list
                </CCText>
              </Box>

              <CCPackageTable
                headers={[]}
                tableData={playerTableData}
                flexArr={[1, 1, 4, 2]}
                handleRowPress={handleRowPressPlayerList}
                borderBottomColor={CCColors.BorderBottom}
                headerTextColor={CCColors.TextColor.Grey}
                margin={0}
                jsxTable={false}
                isMargin={false} // Enables Pressable rows
                tournament={true}
                backgroundColorRow={true}
                tableBorder={false}
                higlightedRow={highlightedRowIndex}
              />
            </Box>
          ) : (
            <>
              <CCPackageTable
                headers={headers}
                tableData={tableData}
                flexArr={customFlexArr}
                handleRowPress={handleRowPress}
                borderBottomColor={CCColors.BorderBottom}
                headerTextColor={CCColors.TextColor.Grey}
                margin={0}
                jsxTable={false}
                isMargin={false} // Enables Pressable rows
                tournament={true}
                backgroundColorRow={true}
                tableBorder={false}
                higlightedRow={highlightedRowIndex}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                paginate={(page: number) => {
                  setCurrentPage(page);
                  setPlayerUserId(null);
                }}
                maxPage={5}
              />
            </>
          )
        ) : (
          <View
            style={{
              alignItems: 'center',
              justifyContent: isMobileOrTab ? 'center' : 'flex-start',
              paddingHorizontal: 20,
              paddingBottom: 20,
              paddingTop: isMobileOrTab ? 20 : 10,
              marginTop: isMobileOrTab ? 0 : '2%',
            }}>
            <Image
              resizeMode="contain"
              style={{
                width: isMobileOrTab ? 180 : 250,
                height: isMobileOrTab ? 180 : 250,
                marginBottom: 15,
              }}
              source={NoStanding}
            />
            <CCText fontSize={24} style={{fontWeight: 500}}>
              No standing found
            </CCText>
            <CCText
              fontSize={14}
              color={CCColors?.TextColor?.Dark}
              style={{fontWeight: 400, paddingBottom: 10}}>
              Standings will appear here after one round.
            </CCText>
          </View>
        )}
      </ScrollView>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {padding: 16, flex: 1},
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
    justifyContent: 'space-between',
    marginBottom: 32,
  },
  card: {
    width: '48%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: CCColors?.CCTableBg,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 2,
    elevation: 1,
  },
  cardLabel: {fontWeight: '400'},
  cardValue: {fontWeight: '500'},

  sectionHeader: {
    fontWeight: '500',
    marginBottom: 10,
  },

  rulesContainer: {
    borderRadius: 8,
    elevation: 2,
    marginBottom: 32,
  },
  ruleItem: {fontWeight: '400', marginBottom: 4},

  playersContainer: {
    elevation: 2,
    marginHorizontal: 10,
  },
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  playerRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: updateColorWithOpacity(CCColors?.Grey?.Grey, 0.3),
    paddingBottom: 12,
  },
  rankWrapper: {
    width: 50,
    display: 'flex',
    flexDirection: 'row',
    // alignItems: 'flex-end',
    marginRight: 5,
  },
  rank: {
    fontWeight: '500',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  usernameRatingWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  username: {
    fontWeight: '500',
  },
  rating: {
    fontWeight: '500',
  },
  score: {
    fontSize: 16,
    color: '#777',
  },
  viewAll: {
    fontWeight: '500',
    marginTop: 8,
  },
  noPlayers: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 16,
    color: '#999',
  },
  tableRowStyle: {
    padding: vs(10),
    width: '100%',
    height: '100%',
    alignItems: 'center',
  },
  nameStyle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
});

export default Standings;
